import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import {
	TPgAirwallexAccount,
	TPgClientAccessSettings,
	TPgClientAccessSettingsSaved,
	TPgFeeMatrix,
	TPgFxSettings,
	TPgLegalEntity,
	TPgListRequest,
	TPgListResponse,
	TPgPayabilityRule,
	TPgRecurringCycle,
	TPgRecurringRun,
	TPgScheduleSettings,
	TPgService,
	TPgServiceTypeMapping,
} from 'services/api/paymentGateway/types';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	createPgLegalEntityRequest,
	createPgPayabilityRuleRequest,
	createPgServiceRequest,
	deletePgPayabilityRuleRequest,
	deletePgServiceTypeMappingRequest,
	getPgAirwallexAccountsFailure,
	getPgAirwallexAccountsRequest,
	getPgAirwallexAccountsSuccess,
	getPgClientAccessFailure,
	getPgClientAccessRequest,
	getPgClientAccessSuccess,
	getPgFeeMatrixFailure,
	getPgFeeMatrixRequest,
	getPgFeeMatrixSuccess,
	getPgFxSettingsFailure,
	getPgFxSettingsRequest,
	getPgFxSettingsSuccess,
	getPgLegalEntitiesFailure,
	getPgLegalEntitiesRequest,
	getPgLegalEntitiesSuccess,
	getPgPayabilityRulesFailure,
	getPgPayabilityRulesRequest,
	getPgPayabilityRulesSuccess,
	getPgRecurringCyclesFailure,
	getPgRecurringCyclesRequest,
	getPgRecurringCyclesSuccess,
	getPgRecurringRunsFailure,
	getPgRecurringRunsRequest,
	getPgRecurringRunsSuccess,
	getPgScheduleFailure,
	getPgScheduleRequest,
	getPgScheduleSuccess,
	getPgServicesFailure,
	getPgServicesRequest,
	getPgServicesSuccess,
	getPgServiceTypeMappingsFailure,
	getPgServiceTypeMappingsRequest,
	getPgServiceTypeMappingsSuccess,
	pgSaveFailure,
	pgSaveSuccess,
	refreshPgRecurringRunsRequest,
	updatePgAirwallexAccountRequest,
	updatePgClientAccessRequest,
	updatePgFeesRequest,
	updatePgFxSettingsRequest,
	updatePgLegalEntityRequest,
	updatePgPayabilityRuleRequest,
	updatePgScheduleRequest,
	updatePgServiceRequest,
	upsertPgServiceTypeMappingRequest,
} from './reducer';
import { selectPgRecurringRunsLastParams } from './selectors';
import {
	TPgCreateLegalEntityPayload,
	TPgCreatePayabilityRulePayload,
	TPgCreateServicePayload,
	TPgSaveKey,
	TPgUpdateAirwallexAccountPayload,
	TPgUpdateClientAccessPayload,
	TPgUpdateFeesPayload,
	TPgUpdateFxSettingsPayload,
	TPgUpdateLegalEntityPayload,
	TPgUpdatePayabilityRulePayload,
	TPgUpdateSchedulePayload,
	TPgUpdateServicePayload,
	TPgUpsertServiceTypeMappingPayload,
} from './types';

const DEFAULT_RUNS_PARAMS: TPgListRequest = { pageNumber: 1, pageSize: PAGE_SIZE };

/** Shows the gateway `message` (or the 403 hint), falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	notificationContainer(getPgErrorMessage(error, fallback), 'error');
}

// =============================================================
// Loaders (also called after a save to refresh the dependent lists)
// =============================================================

function* loadServices() {
	const response: TPgService[] = yield call(api.paymentGateway.getServices);
	yield put(getPgServicesSuccess(response));
}

function* loadServiceTypeMappings() {
	const response: TPgServiceTypeMapping[] = yield call(api.paymentGateway.getServiceTypeMappings);
	yield put(getPgServiceTypeMappingsSuccess(response));
}

function* loadPayabilityRules() {
	const response: TPgPayabilityRule[] = yield call(api.paymentGateway.getPayabilityRules);
	yield put(getPgPayabilityRulesSuccess(response));
}

function* loadLegalEntities() {
	const response: TPgLegalEntity[] = yield call(api.paymentGateway.getLegalEntities);
	yield put(getPgLegalEntitiesSuccess(response));
}

function* loadAirwallexAccounts() {
	const response: TPgAirwallexAccount[] = yield call(api.paymentGateway.getAirwallexAccounts);
	yield put(getPgAirwallexAccountsSuccess(response));
}

/** Runs `effect`; on failure clears the loading flag and shows the error. */
function* load(effect: () => Generator, failure: { type: string }, fallback: string) {
	try {
		yield call(effect);
	} catch (error) {
		yield put(failure);
		notifyError(error, fallback);
	}
}

/**
 * Saves with `effect`, then refreshes `reloads` (a failed refresh does not undo the save) and
 * raises the `saved` flag that closes the dialog.
 */
function* save(
	key: TPgSaveKey,
	effect: () => Generator,
	reloads: (() => Generator)[],
	successMessage: string,
	fallback: string,
) {
	try {
		yield call(effect);
	} catch (error) {
		yield put(pgSaveFailure(key));
		notifyError(error, fallback);
		return;
	}
	try {
		yield all(reloads.map((reload) => call(reload)));
	} catch (error) {
		notifyError(error, 'Saved, but failed to reload the data');
	}
	yield put(pgSaveSuccess(key));
	notificationContainer(successMessage, 'success');
}

// =============================================================
// Services and mappings
// =============================================================

function* getServicesWorker() {
	yield call(load, loadServices, getPgServicesFailure(), 'Failed to load services');
}

function* getServiceTypeMappingsWorker() {
	yield call(
		load,
		loadServiceTypeMappings,
		getPgServiceTypeMappingsFailure(),
		'Failed to load service type mappings',
	);
}

function* createServiceWorker({ payload }: PayloadAction<TPgCreateServicePayload>) {
	yield call(
		save,
		'service',
		function* createService() {
			yield call(api.paymentGateway.createService, payload);
		},
		[loadServices],
		'Service was created',
		'Failed to create the service',
	);
}

function* updateServiceWorker({ payload }: PayloadAction<TPgUpdateServicePayload>) {
	yield call(
		save,
		'service',
		function* updateService() {
			yield call(api.paymentGateway.updateService, payload);
		},
		[loadServices],
		'Service was updated',
		'Failed to update the service',
	);
}

function* upsertServiceTypeMappingWorker({
	payload,
}: PayloadAction<TPgUpsertServiceTypeMappingPayload>) {
	yield call(
		save,
		'serviceTypeMapping',
		function* upsertMapping() {
			yield call(api.paymentGateway.upsertServiceTypeMapping, payload);
		},
		[loadServiceTypeMappings, loadServices],
		`Service type '${payload.serviceType}' was mapped`,
		'Failed to save the mapping',
	);
}

function* deleteServiceTypeMappingWorker({ payload }: PayloadAction<string>) {
	yield call(
		save,
		'serviceTypeMapping',
		function* deleteMapping() {
			yield call(api.paymentGateway.deleteServiceTypeMapping, payload);
		},
		[loadServiceTypeMappings, loadServices],
		`Mapping of service type '${payload}' was removed`,
		'Failed to remove the mapping',
	);
}

// =============================================================
// Debt transfer rules
// =============================================================

function* getPayabilityRulesWorker() {
	yield call(
		load,
		loadPayabilityRules,
		getPgPayabilityRulesFailure(),
		'Failed to load debt transfer rules',
	);
}

function* createPayabilityRuleWorker({ payload }: PayloadAction<TPgCreatePayabilityRulePayload>) {
	yield call(
		save,
		'payabilityRule',
		function* createRule() {
			yield call(api.paymentGateway.createPayabilityRule, payload);
		},
		[loadPayabilityRules],
		'Rule was created',
		'Failed to create the rule',
	);
}

function* updatePayabilityRuleWorker({ payload }: PayloadAction<TPgUpdatePayabilityRulePayload>) {
	yield call(
		save,
		'payabilityRule',
		function* updateRule() {
			yield call(api.paymentGateway.updatePayabilityRule, payload);
		},
		[loadPayabilityRules],
		'Rule was updated',
		'Failed to update the rule',
	);
}

function* deletePayabilityRuleWorker({ payload }: PayloadAction<number>) {
	yield call(
		save,
		'payabilityRule',
		function* deleteRule() {
			yield call(api.paymentGateway.deletePayabilityRule, payload);
		},
		[loadPayabilityRules],
		'Rule was deleted',
		'Failed to delete the rule',
	);
}

// =============================================================
// Legal entities and Airwallex accounts
// =============================================================

function* getLegalEntitiesWorker() {
	yield call(load, loadLegalEntities, getPgLegalEntitiesFailure(), 'Failed to load legal entities');
}

function* createLegalEntityWorker({ payload }: PayloadAction<TPgCreateLegalEntityPayload>) {
	yield call(
		save,
		'legalEntity',
		function* createLegalEntity() {
			yield call(api.paymentGateway.createLegalEntity, payload);
		},
		[loadLegalEntities],
		'Legal entity was created',
		'Failed to create the legal entity',
	);
}

function* updateLegalEntityWorker({ payload }: PayloadAction<TPgUpdateLegalEntityPayload>) {
	yield call(
		save,
		'legalEntity',
		function* updateLegalEntity() {
			yield call(api.paymentGateway.updateLegalEntity, payload);
		},
		[loadLegalEntities],
		'Legal entity was updated',
		'Failed to update the legal entity',
	);
}

function* getAirwallexAccountsWorker() {
	yield call(
		load,
		loadAirwallexAccounts,
		getPgAirwallexAccountsFailure(),
		'Failed to load Airwallex accounts',
	);
}

function* updateAirwallexAccountWorker({
	payload,
}: PayloadAction<TPgUpdateAirwallexAccountPayload>) {
	yield call(
		save,
		'airwallexAccount',
		function* updateAccount() {
			yield call(api.paymentGateway.updateAirwallexAccount, payload);
		},
		[loadAirwallexAccounts],
		'Airwallex account was updated',
		'Failed to update the Airwallex account',
	);
}

// =============================================================
// Fees and DD FX
// =============================================================

function* getFeeMatrixWorker({ payload }: PayloadAction<number | null>) {
	try {
		const response: TPgFeeMatrix = yield call(api.paymentGateway.getFees, payload);
		yield put(getPgFeeMatrixSuccess(response));
	} catch (error) {
		yield put(getPgFeeMatrixFailure());
		notifyError(error, 'Failed to load fees');
	}
}

function* updateFeesWorker({ payload }: PayloadAction<TPgUpdateFeesPayload>) {
	try {
		const response: TPgFeeMatrix = yield call(api.paymentGateway.updateFees, payload);
		yield put(getPgFeeMatrixSuccess(response));
		yield put(pgSaveSuccess('fees'));
		notificationContainer('Fees were saved', 'success');
	} catch (error) {
		yield put(pgSaveFailure('fees'));
		notifyError(error, 'Failed to save fees');
	}
}

function* getFxSettingsWorker({ payload }: PayloadAction<number | null>) {
	try {
		const response: TPgFxSettings = yield call(api.paymentGateway.getFxSettings, payload);
		yield put(getPgFxSettingsSuccess(response));
	} catch (error) {
		yield put(getPgFxSettingsFailure());
		notifyError(error, 'Failed to load FX settings');
	}
}

function* updateFxSettingsWorker({ payload }: PayloadAction<TPgUpdateFxSettingsPayload>) {
	try {
		const response: TPgFxSettings = yield call(api.paymentGateway.updateFxSettings, payload);
		yield put(getPgFxSettingsSuccess(response));
		yield put(pgSaveSuccess('fxSettings'));
		notificationContainer('FX settings were saved', 'success');
	} catch (error) {
		yield put(pgSaveFailure('fxSettings'));
		notifyError(error, 'Failed to save FX settings');
	}
}

// =============================================================
// Schedule, runs and cycles
// =============================================================

function* getScheduleWorker() {
	try {
		const response: TPgScheduleSettings = yield call(api.paymentGateway.getSchedule);
		yield put(getPgScheduleSuccess(response));
	} catch (error) {
		yield put(getPgScheduleFailure());
		notifyError(error, 'Failed to load the schedule');
	}
}

function* updateScheduleWorker({ payload }: PayloadAction<TPgUpdateSchedulePayload>) {
	try {
		const response: TPgScheduleSettings = yield call(api.paymentGateway.updateSchedule, payload);
		yield put(getPgScheduleSuccess(response));
		yield put(pgSaveSuccess('schedule'));
		notificationContainer('Schedule was saved', 'success');
	} catch (error) {
		yield put(pgSaveFailure('schedule'));
		notifyError(error, 'Failed to save the schedule');
	}
}

// =============================================================
// Customer access, EAS updates pause and customer e-mails
// =============================================================

function* getClientAccessWorker() {
	try {
		const response: TPgClientAccessSettings = yield call(api.paymentGateway.getClientAccess);
		yield put(getPgClientAccessSuccess(response));
	} catch (error) {
		yield put(getPgClientAccessFailure());
		notifyError(error, 'Failed to load access and notification settings');
	}
}

function* updateClientAccessWorker({ payload }: PayloadAction<TPgUpdateClientAccessPayload>) {
	try {
		const response: TPgClientAccessSettingsSaved = yield call(
			api.paymentGateway.updateClientAccess,
			payload,
		);
		const { requeuedEasUpdates, ...settings } = response;
		yield put(getPgClientAccessSuccess(settings));
		yield put(pgSaveSuccess('clientAccess'));
		notificationContainer('Access and notification settings were saved', 'success');
		if (requeuedEasUpdates > 0) {
			notificationContainer(
				`${requeuedEasUpdates} queued EAS update${requeuedEasUpdates === 1 ? ' was' : 's were'} sent`,
				'info',
			);
		}
	} catch (error) {
		yield put(pgSaveFailure('clientAccess'));
		notifyError(error, 'Failed to save access and notification settings');
	}
}

function* getRecurringRunsWorker({ payload }: PayloadAction<TPgListRequest>) {
	try {
		const response: TPgListResponse<TPgRecurringRun> = yield call(
			api.paymentGateway.getRecurringRuns,
			payload,
		);
		yield put(getPgRecurringRunsSuccess(response));
	} catch (error) {
		yield put(getPgRecurringRunsFailure());
		notifyError(error, 'Failed to load runs');
	}
}

function* refreshRecurringRunsWorker() {
	const lastParams: TPgListRequest | null = yield select(selectPgRecurringRunsLastParams);
	yield call(getRecurringRunsWorker, {
		type: getPgRecurringRunsRequest.type,
		payload: lastParams ?? DEFAULT_RUNS_PARAMS,
	});
}

function* getRecurringCyclesWorker({ payload }: PayloadAction<TPgListRequest>) {
	try {
		const response: TPgListResponse<TPgRecurringCycle> = yield call(
			api.paymentGateway.getRecurringCycles,
			payload,
		);
		yield put(getPgRecurringCyclesSuccess(response));
	} catch (error) {
		yield put(getPgRecurringCyclesFailure());
		notifyError(error, 'Failed to load monthly cycles');
	}
}

export function* pgSettingsSaga() {
	yield takeLatest(getPgServicesRequest.type, getServicesWorker);
	yield takeLatest(createPgServiceRequest.type, createServiceWorker);
	yield takeLatest(updatePgServiceRequest.type, updateServiceWorker);
	yield takeLatest(getPgServiceTypeMappingsRequest.type, getServiceTypeMappingsWorker);
	// Several mapping rows may be saved one after another: none of them may be dropped.
	yield takeEvery(upsertPgServiceTypeMappingRequest.type, upsertServiceTypeMappingWorker);
	yield takeEvery(deletePgServiceTypeMappingRequest.type, deleteServiceTypeMappingWorker);
	yield takeLatest(getPgPayabilityRulesRequest.type, getPayabilityRulesWorker);
	yield takeLatest(createPgPayabilityRuleRequest.type, createPayabilityRuleWorker);
	yield takeLatest(updatePgPayabilityRuleRequest.type, updatePayabilityRuleWorker);
	yield takeEvery(deletePgPayabilityRuleRequest.type, deletePayabilityRuleWorker);
	yield takeLatest(getPgLegalEntitiesRequest.type, getLegalEntitiesWorker);
	yield takeLatest(createPgLegalEntityRequest.type, createLegalEntityWorker);
	yield takeLatest(updatePgLegalEntityRequest.type, updateLegalEntityWorker);
	yield takeLatest(getPgAirwallexAccountsRequest.type, getAirwallexAccountsWorker);
	yield takeLatest(updatePgAirwallexAccountRequest.type, updateAirwallexAccountWorker);
	yield takeLatest(getPgFeeMatrixRequest.type, getFeeMatrixWorker);
	yield takeLatest(updatePgFeesRequest.type, updateFeesWorker);
	yield takeLatest(getPgFxSettingsRequest.type, getFxSettingsWorker);
	yield takeLatest(updatePgFxSettingsRequest.type, updateFxSettingsWorker);
	yield takeLatest(getPgScheduleRequest.type, getScheduleWorker);
	yield takeLatest(updatePgScheduleRequest.type, updateScheduleWorker);
	yield takeLatest(getPgClientAccessRequest.type, getClientAccessWorker);
	yield takeLatest(updatePgClientAccessRequest.type, updateClientAccessWorker);
	yield takeLatest(getPgRecurringRunsRequest.type, getRecurringRunsWorker);
	yield takeLatest(refreshPgRecurringRunsRequest.type, refreshRecurringRunsWorker);
	yield takeLatest(getPgRecurringCyclesRequest.type, getRecurringCyclesWorker);
}
